/**
 * SC-02: 案件一覧
 *
 * 営業案件の一覧を表示し、検索・フィルタリング機能を提供
 *
 * @created 2025-11-16
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  InputAdornment,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  GetApp,
  Edit,
  FilterList,
  Visibility,
  Close,
  ExpandMore,
  ExpandLess,
  Person,
  CalendarMonth,
  Warning,
  AttachMoney,
  Schedule,
  FiberNew,
  CloudUpload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { salesCasesApi, salesMasterApi, salesExportApi } from '../../services/salesApi';
import type { SalesCase, SalesPhase, SalesAssignee } from '../../services/salesApi';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateJST } from '../../utils/dateUtils';
import AsyncAutocomplete from '../../components/Common/AsyncAutocomplete';

// クイックフィルタの定義
type QuickFilterType = 'my_cases' | 'this_month_order' | 'stagnant' | 'overdue_action' | 'large_cases' | 'updated_this_week';

// 年度フィルタの定義
type FiscalYearType = 'current' | 'next';

interface QuickFilter {
  key: QuickFilterType;
  label: string;
  icon: React.ReactElement;
  description: string;
}

const QUICK_FILTERS: QuickFilter[] = [
  { key: 'my_cases', label: '自分の案件', icon: <Person fontSize="small" />, description: '自分が担当の案件' },
  { key: 'updated_this_week', label: '今週更新', icon: <FiberNew fontSize="small" />, description: '今週更新があった案件' },
  { key: 'this_month_order', label: '今月受注予定', icon: <CalendarMonth fontSize="small" />, description: '受注予定日が今月の案件' },
  { key: 'stagnant', label: '滞留案件', icon: <Schedule fontSize="small" />, description: 'フェーズ目標日数を超過している案件（実行除く）' },
  { key: 'overdue_action', label: '要アクション', icon: <Warning fontSize="small" />, description: '次回アクション期限超過' },
  { key: 'large_cases', label: '大型案件', icon: <AttachMoney fontSize="small" />, description: '1,000万円以上の案件' },
];

// 当年度・次年度の年を取得
const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

const BUSINESS_CATEGORIES = ['日本国内開発', 'SI経由開発', '商品・サービス販売', 'SES'] as const;

const APPROVAL_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: '全て' },
  { value: 'none', label: '通常' },
  { value: 'draft', label: '新規下書き' },
  { value: 'new_pending', label: '新規承認待ち' },
  { value: 'pending', label: '変更承認待ち' },
  { value: 'rejected', label: '却下' },
];

// 適用中フィルタの型
interface ActiveFilter {
  key: string;
  label: string;
  onRemove: () => void;
}

const SalesCasesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, salesPermissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cases, setCases] = useState<SalesCase[]>([]);
  const [phases, setPhases] = useState<SalesPhase[]>([]);
  const [showDetailedFilter, setShowDetailedFilter] = useState(false);

  const canManageCases = salesPermissions?.can_manage_cases || false;

  // クイックフィルタ
  const [activeQuickFilters, setActiveQuickFilters] = useState<Set<QuickFilterType>>(new Set());

  // 年度フィルタ（デフォルト: 当年度）
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<FiscalYearType>('current');

  // 検索条件
  const [searchCaseName, setSearchCaseName] = useState('');
  const [searchAccount, setSearchAccount] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterEmployeeObj, setFilterEmployeeObj] = useState<SalesAssignee | null>(null);
  const [filterPhases, setFilterPhases] = useState<number[]>([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterApprovalStatus, setFilterApprovalStatus] = useState('all');
  const [filterBusinessCategories, setFilterBusinessCategories] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  // ソート
  const [sortBy, setSortBy] = useState<string>('account_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // ページネーション
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalCases, setTotalCases] = useState(0);

  // 初期化完了フラグ（フィルター復元が完了してからAPIを呼ぶため）
  const [isInitialized, setIsInitialized] = useState(false);
  // フィルター保存を防ぐためのフラグ
  const skipNextSave = useRef(true);

  // sessionStorageからフィルター状態を復元（初回のみ）
  useEffect(() => {
    const saved = sessionStorage.getItem('salesCasesFilters');
    if (saved) {
      try {
        const filters = JSON.parse(saved);
        if (filters.searchCaseName) setSearchCaseName(filters.searchCaseName);
        if (filters.searchAccount) setSearchAccount(filters.searchAccount);
        if (filters.filterEmployee) setFilterEmployee(filters.filterEmployee);
        if (filters.filterEmployeeObj) setFilterEmployeeObj(filters.filterEmployeeObj);
        if (Array.isArray(filters.filterPhases)) {
          setFilterPhases(filters.filterPhases);
        } else if (filters.filterPhase !== undefined && filters.filterPhase !== 'all') {
          setFilterPhases([Number(filters.filterPhase)]);
        }
        if (filters.filterStatus) setFilterStatus(filters.filterStatus);
        if (filters.filterApprovalStatus) setFilterApprovalStatus(filters.filterApprovalStatus);
        if (Array.isArray(filters.filterBusinessCategories)) {
          setFilterBusinessCategories(filters.filterBusinessCategories);
        }
        if (filters.dateFrom) setDateFrom(filters.dateFrom);
        if (filters.dateTo) setDateTo(filters.dateTo);
        if (filters.amountMin) setAmountMin(filters.amountMin);
        if (filters.amountMax) setAmountMax(filters.amountMax);
        if (filters.activeQuickFilters) {
          const restored = new Set(filters.activeQuickFilters as QuickFilterType[]);
          setActiveQuickFilters(restored);
          if (restored.has('this_month_order') && !filters.dateFrom && !filters.dateTo) {
            const now = new Date();
            const y = now.getFullYear();
            const m = now.getMonth();
            const pad = (n: number) => String(n).padStart(2, '0');
            const last = new Date(y, m + 1, 0).getDate();
            setDateFrom(`${y}-${pad(m + 1)}-01`);
            setDateTo(`${y}-${pad(m + 1)}-${pad(last)}`);
          }
          if (restored.has('large_cases') && !filters.amountMin) {
            setAmountMin('1000');
          }
        }
        if (filters.selectedFiscalYear) setSelectedFiscalYear(filters.selectedFiscalYear);
        if (filters.page) setPage(filters.page);
        if (filters.sortBy) setSortBy(filters.sortBy);
        if (filters.sortOrder) setSortOrder(filters.sortOrder);
      } catch (err) {
        console.error('フィルター復元エラー:', err);
      }
    }
    // 復元完了後、次のレンダリングサイクルで初期化完了とする
    // setTimeout を使って次のレンダリングサイクルまで待つ
    setTimeout(() => {
      skipNextSave.current = false;
      setIsInitialized(true);
    }, 0);
  }, []);

  // フィルター状態をsessionStorageに保存
  useEffect(() => {
    // 初期化完了前は保存しない
    if (skipNextSave.current) return;

    const filtersToSave = {
      searchCaseName,
      searchAccount,
      filterEmployee,
      filterEmployeeObj,
      filterPhases,
      filterStatus,
      filterApprovalStatus,
      filterBusinessCategories,
      dateFrom,
      dateTo,
      amountMin,
      amountMax,
      activeQuickFilters: Array.from(activeQuickFilters),
      selectedFiscalYear,
      page,
      sortBy,
      sortOrder,
    };
    sessionStorage.setItem('salesCasesFilters', JSON.stringify(filtersToSave));
  }, [
    searchCaseName, searchAccount, filterEmployee, filterEmployeeObj,
    filterPhases, filterStatus, filterApprovalStatus, filterBusinessCategories,
    dateFrom, dateTo, amountMin, amountMax,
    activeQuickFilters, selectedFiscalYear, page, sortBy, sortOrder
  ]);

  useEffect(() => {
    fetchPhases();
  }, []);

  const fetchPhases = async () => {
    try {
      const response = await salesMasterApi.getPhases();
      if (response.data.success) {
        setPhases(response.data.data);
      }
    } catch (err) {
      console.error('フェーズ取得エラー:', err);
    }
  };

  // 今月の開始日と終了日を取得（ローカルTZ基準）
  const getThisMonthRange = useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: fmt(firstDay), to: fmt(lastDay) };
  }, []);

  const buildListParams = useCallback(() => {
    const params: Record<string, string | number | boolean | undefined> = {};

    if (searchCaseName) params.search = searchCaseName;
    if (searchAccount) params.account_name = searchAccount;
    if (filterPhases.length > 0) params.sales_phase_ids = filterPhases.join(',');
    if (filterStatus && filterStatus !== 'all') {
      if (filterStatus === 'closed_won') {
        params.status = 'closed';
        params.close_result = 'won';
      } else if (filterStatus === 'closed_lost') {
        params.status = 'closed';
        params.close_result = 'lost';
      } else {
        params.status = filterStatus;
      }
    }
    if (filterApprovalStatus && filterApprovalStatus !== 'all') {
      params.approval_status = filterApprovalStatus;
    }
    if (filterBusinessCategories.length > 0) {
      params.business_category = filterBusinessCategories.join(',');
    }
    if (dateFrom) params.expected_order_date_from = dateFrom;
    if (dateTo) params.expected_order_date_to = dateTo;
    if (amountMin) params.expected_order_amount_min = parseFloat(amountMin) * 10000;
    if (amountMax) params.expected_order_amount_max = parseFloat(amountMax) * 10000;
    if (filterEmployee) params.assigned_employee_id = filterEmployee;
    if (activeQuickFilters.has('stagnant')) params.stagnant = true;
    if (activeQuickFilters.has('overdue_action')) params.next_action_overdue = true;
    if (activeQuickFilters.has('updated_this_week')) params.updated_this_week = true;

    return params;
  }, [
    searchCaseName, searchAccount, filterEmployee, filterPhases, filterStatus,
    filterApprovalStatus, filterBusinessCategories, dateFrom, dateTo, amountMin, amountMax,
    activeQuickFilters,
  ]);

  const amountRangeInvalid = Boolean(
    amountMin && amountMax && parseFloat(amountMin) > parseFloat(amountMax)
  );

  const fetchCases = useCallback(async () => {
    if (amountRangeInvalid) {
      setError('金額下限が上限を超えています');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const params = {
        ...buildListParams(),
        page,
        limit: itemsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        fiscal_year: selectedFiscalYear === 'current' ? currentYear : nextYear,
      };

      const response = await salesCasesApi.getCases(params);

      if (response.data.success) {
        setCases(response.data.data.cases);
        setTotalCases(response.data.data.pagination.total_count);
      }
    } catch (err) {
      console.error('案件一覧取得エラー:', err);
      setError(extractErrorMessage(err, '案件一覧の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, [
    page, itemsPerPage, sortBy, sortOrder, selectedFiscalYear,
    buildListParams, amountRangeInvalid,
  ]);

  // ページまたはフィルターが変更されたらデータを再取得
  // 初期化完了後にのみ実行
  useEffect(() => {
    if (!isInitialized) return;
    fetchCases();
  }, [fetchCases, isInitialized]);

  // クイックと詳細の重なる軸を同期（自分の案件 / 今月受注予定 / 大型）
  useEffect(() => {
    const { from, to } = getThisMonthRange();
    setActiveQuickFilters((prev) => {
      const next = new Set(prev);
      if (filterEmployee && user?.employee_id && filterEmployee === user.employee_id) {
        next.add('my_cases');
      } else {
        next.delete('my_cases');
      }
      if (dateFrom === from && dateTo === to) {
        next.add('this_month_order');
      } else {
        next.delete('this_month_order');
      }
      const min = amountMin ? parseFloat(amountMin) : NaN;
      if (!isNaN(min) && min >= 1000) {
        next.add('large_cases');
      } else {
        next.delete('large_cases');
      }
      const unchanged =
        next.size === prev.size && Array.from(next).every((k) => prev.has(k));
      return unchanged ? prev : next;
    });
  }, [filterEmployee, dateFrom, dateTo, amountMin, user?.employee_id, getThisMonthRange]);

  const toggleQuickFilter = (filterKey: QuickFilterType) => {
    const turningOn = !activeQuickFilters.has(filterKey);

    if (filterKey === 'my_cases') {
      if (turningOn && user?.employee_id) {
        setFilterEmployee(user.employee_id);
        setFilterEmployeeObj({ employee_id: user.employee_id, name: user.name });
      } else {
        setFilterEmployee('');
        setFilterEmployeeObj(null);
      }
    } else if (filterKey === 'this_month_order') {
      if (turningOn) {
        const { from, to } = getThisMonthRange();
        setDateFrom(from);
        setDateTo(to);
      } else {
        setDateFrom('');
        setDateTo('');
      }
    } else if (filterKey === 'large_cases') {
      if (turningOn) {
        setAmountMin('1000');
      } else {
        setAmountMin('');
      }
    } else {
      setActiveQuickFilters((prev) => {
        const next = new Set(prev);
        if (next.has(filterKey)) next.delete(filterKey);
        else next.add(filterKey);
        return next;
      });
    }
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchCaseName('');
    setSearchAccount('');
    setFilterEmployee('');
    setFilterEmployeeObj(null);
    setFilterPhases([]);
    setFilterStatus('all');
    setFilterApprovalStatus('all');
    setFilterBusinessCategories([]);
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setActiveQuickFilters(new Set());
    setSelectedFiscalYear('current');
    setSortBy('account_name');
    setSortOrder('asc');
    setPage(1);
  };

  const handleExportCsv = async () => {
    if (amountRangeInvalid) {
      setError('金額下限が上限を超えています');
      return;
    }
    try {
      const blob = await salesExportApi.exportCsv(buildListParams());

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales_cases_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV出力エラー:', err);
      setError(extractErrorMessage(err, 'CSV出力に失敗しました'));
    }
  };

  const handleExportMonday = async () => {
    try {
      const blob = await salesExportApi.exportMonday();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `monday_export_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Monday出力エラー:', err);
      setError(extractErrorMessage(err, 'Monday連携データの出力に失敗しました'));
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return formatDateJST(dateString);
  };

  // ソート変更ハンドラ
  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // 適用中フィルタの一覧を生成
  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const filters: ActiveFilter[] = [];

    // クイックフィルタ
    activeQuickFilters.forEach((key) => {
      const qf = QUICK_FILTERS.find((f) => f.key === key);
      if (qf) {
        filters.push({
          key: `quick_${key}`,
          label: qf.label,
          onRemove: () => toggleQuickFilter(key),
        });
      }
    });

    // 詳細フィルタ
    if (searchCaseName) {
      filters.push({
        key: 'searchCaseName',
        label: `案件名: ${searchCaseName}`,
        onRemove: () => setSearchCaseName(''),
      });
    }
    if (searchAccount) {
      filters.push({
        key: 'searchAccount',
        label: `取引先: ${searchAccount}`,
        onRemove: () => setSearchAccount(''),
      });
    }
    if (filterEmployee && filterEmployeeObj && !activeQuickFilters.has('my_cases')) {
      filters.push({
        key: 'filterEmployee',
        label: `担当者: ${filterEmployeeObj.name}`,
        onRemove: () => { setFilterEmployee(''); setFilterEmployeeObj(null); },
      });
    }
    if (filterPhases.length > 0) {
      const names = filterPhases
        .map((id) => phases.find((p) => p.phase_id === id)?.phase_name || String(id))
        .join(', ');
      filters.push({
        key: 'filterPhases',
        label: `フェーズ: ${names}`,
        onRemove: () => setFilterPhases([]),
      });
    }
    if (filterStatus && filterStatus !== 'all') {
      const statusLabel = filterStatus === 'active' ? '商談中' : 
                          filterStatus === 'suspended' ? '保留' : 
                          filterStatus === 'closed_won' ? '受注' : 
                          filterStatus === 'closed_lost' ? '失注' : filterStatus;
      filters.push({
        key: 'filterStatus',
        label: `状態: ${statusLabel}`,
        onRemove: () => setFilterStatus('all'),
      });
    }
    if (filterApprovalStatus && filterApprovalStatus !== 'all') {
      const approvalLabel = APPROVAL_STATUS_OPTIONS.find((o) => o.value === filterApprovalStatus)?.label
        || filterApprovalStatus;
      filters.push({
        key: 'filterApprovalStatus',
        label: `承認: ${approvalLabel}`,
        onRemove: () => setFilterApprovalStatus('all'),
      });
    }
    if (filterBusinessCategories.length > 0) {
      filters.push({
        key: 'filterBusinessCategories',
        label: `事業方向: ${filterBusinessCategories.join(', ')}`,
        onRemove: () => setFilterBusinessCategories([]),
      });
    }
    if (dateFrom && !activeQuickFilters.has('this_month_order')) {
      filters.push({
        key: 'dateFrom',
        label: `受注予定日From: ${dateFrom}`,
        onRemove: () => setDateFrom(''),
      });
    }
    if (dateTo && !activeQuickFilters.has('this_month_order')) {
      filters.push({
        key: 'dateTo',
        label: `受注予定日To: ${dateTo}`,
        onRemove: () => setDateTo(''),
      });
    }
    if (amountMin && !(activeQuickFilters.has('large_cases') && amountMin === '1000')) {
      filters.push({
        key: 'amountMin',
        label: `金額下限: ${amountMin}万円`,
        onRemove: () => setAmountMin(''),
      });
    }
    if (amountMax) {
      filters.push({
        key: 'amountMax',
        label: `金額上限: ${amountMax}万円`,
        onRemove: () => setAmountMax(''),
      });
    }
    if (selectedFiscalYear === 'next') {
      filters.push({
        key: 'selectedFiscalYear',
        label: `表示: ${nextYear}年度計上`,
        onRemove: () => setSelectedFiscalYear('current'),
      });
    }

    return filters;
  }, [
    activeQuickFilters, searchCaseName, searchAccount, filterEmployee, filterEmployeeObj,
    filterPhases, filterStatus, filterApprovalStatus, filterBusinessCategories,
    dateFrom, dateTo, amountMin, amountMax, phases, selectedFiscalYear,
  ]);

  const totalPages = Math.ceil(totalCases / itemsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          営業案件一覧
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canManageCases && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/sales/cases/new')}
            >
              新規案件登録
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={handleExportCsv}
          >
            CSV出力
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={handleExportMonday}
            color="secondary"
          >
            Monday出力
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* クイックフィルタ */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
            クイックフィルタ:
          </Typography>
          {QUICK_FILTERS.map((qf) => (
            <Tooltip key={qf.key} title={qf.description}>
              <Chip
                icon={qf.icon}
                label={qf.label}
                variant={activeQuickFilters.has(qf.key) ? 'filled' : 'outlined'}
                color={activeQuickFilters.has(qf.key) ? 'primary' : 'default'}
                onClick={() => toggleQuickFilter(qf.key)}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          ))}
          <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 1, ml: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">表示年度</Typography>
            <Tooltip title={`${currentYear}年度の予定計上額を表示（件数は変わりません）`}>
              <Chip
                icon={<CalendarMonth fontSize="small" />}
                label={`当年度(${currentYear})`}
                variant={selectedFiscalYear === 'current' ? 'filled' : 'outlined'}
                color={selectedFiscalYear === 'current' ? 'secondary' : 'default'}
                onClick={() => setSelectedFiscalYear('current')}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
            <Tooltip title={`${nextYear}年度の予定計上額を表示（件数は変わりません）`}>
              <Chip
                icon={<CalendarMonth fontSize="small" />}
                label={`次年度(${nextYear})`}
                variant={selectedFiscalYear === 'next' ? 'filled' : 'outlined'}
                color={selectedFiscalYear === 'next' ? 'secondary' : 'default'}
                onClick={() => setSelectedFiscalYear('next')}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* 詳細フィルタ */}
      <Paper sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: showDetailedFilter ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList fontSize="small" color="action" />
            <Typography variant="subtitle2">詳細フィルタ</Typography>
          </Box>
          <IconButton size="small" onClick={() => setShowDetailedFilter(!showDetailedFilter)}>
            {showDetailedFilter ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={showDetailedFilter}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2.5, pt: 0.5 }}>
              <TextField
                label="案件名・概要"
                value={searchCaseName}
                onChange={(e) => { setSearchCaseName(e.target.value); setPage(1); }}
                size="small"
                fullWidth
              />
              <TextField
                label="取引先"
                value={searchAccount}
                onChange={(e) => { setSearchAccount(e.target.value); setPage(1); }}
                size="small"
                fullWidth
              />
              <AsyncAutocomplete<SalesAssignee>
                label="担当者"
                searchApi={salesCasesApi.searchAssignees}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
                value={filterEmployeeObj}
                onChange={(val) => {
                  if (val && typeof val !== 'string') {
                    setFilterEmployeeObj(val);
                    setFilterEmployee(val.employee_id);
                  } else {
                    setFilterEmployeeObj(null);
                    setFilterEmployee('');
                  }
                  setPage(1);
                }}
                size="small"
                loadOnOpen
                isOptionEqualToValue={(opt, val) => opt.employee_id === val.employee_id}
                placeholder="名前で検索..."
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="filter-phases-label" shrink>フェーズ</InputLabel>
                <Select
                  labelId="filter-phases-label"
                  multiple
                  value={filterPhases}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFilterPhases(typeof val === 'string' ? [] : val.map(Number));
                    setPage(1);
                  }}
                  input={<OutlinedInput notched label="フェーズ" />}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? '全て'
                      : selected.map((id) => phases.find((p) => p.phase_id === id)?.phase_name || id).join(', ')
                  }
                  displayEmpty
                >
                  {phases.map((phase) => (
                    <MenuItem key={phase.phase_id} value={phase.phase_id}>
                      <Checkbox checked={filterPhases.includes(phase.phase_id)} />
                      <ListItemText primary={phase.phase_name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>案件状態</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                  label="案件状態"
                >
                  <MenuItem value="all">全て</MenuItem>
                  <MenuItem value="active">商談中</MenuItem>
                  <MenuItem value="suspended">保留</MenuItem>
                  <MenuItem value="closed_won">受注</MenuItem>
                  <MenuItem value="closed_lost">失注</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>承認状態</InputLabel>
                <Select
                  value={filterApprovalStatus}
                  onChange={(e) => { setFilterApprovalStatus(e.target.value); setPage(1); }}
                  label="承認状態"
                >
                  {APPROVAL_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="filter-business-label" shrink>事業方向</InputLabel>
                <Select
                  labelId="filter-business-label"
                  multiple
                  value={filterBusinessCategories}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFilterBusinessCategories(typeof val === 'string' ? [] : val);
                    setPage(1);
                  }}
                  input={<OutlinedInput notched label="事業方向" />}
                  renderValue={(selected) => selected.length === 0 ? '全て' : selected.join(', ')}
                  displayEmpty
                >
                  {BUSINESS_CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      <Checkbox checked={filterBusinessCategories.includes(cat)} />
                      <ListItemText primary={cat} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="受注予定日From"
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="受注予定日To"
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="金額下限"
                value={amountMin}
                onChange={(e) => { setAmountMin(e.target.value); setPage(1); }}
                size="small"
                type="number"
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">万円</InputAdornment>,
                }}
              />
              <TextField
                label="金額上限"
                value={amountMax}
                onChange={(e) => { setAmountMax(e.target.value); setPage(1); }}
                size="small"
                type="number"
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">万円</InputAdornment>,
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Button variant="outlined" size="small" onClick={handleClearSearch}>
                全てクリア
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* 適用中フィルタの表示 */}
      {activeFilters.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            適用中:
          </Typography>
          {activeFilters.map((af) => (
            <Chip
              key={af.key}
              label={af.label}
              size="small"
              onDelete={af.onRemove}
              deleteIcon={<Close fontSize="small" />}
              variant="outlined"
              color="primary"
            />
          ))}
          <Button size="small" onClick={handleClearSearch} sx={{ ml: 1 }}>
            全てクリア
          </Button>
        </Box>
      )}

      {/* 件数表示 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          全{totalCases}件中 {totalCases > 0 ? (page - 1) * itemsPerPage + 1 : 0}-{Math.min(page * itemsPerPage, totalCases)}件を表示
        </Typography>
      </Box>

      {/* 案件一覧テーブル */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'account_name'}
                    direction={sortBy === 'account_name' ? sortOrder : 'asc'}
                    onClick={() => handleSortChange('account_name')}
                  >
                    顧客名
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>案件名</TableCell>
                <TableCell>フェーズ</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'days_in_phase'}
                    direction={sortBy === 'days_in_phase' ? sortOrder : 'asc'}
                    onClick={() => handleSortChange('days_in_phase')}
                  >
                    滞留
                  </TableSortLabel>
                </TableCell>
                <TableCell>次回アクション</TableCell>
                <TableCell>案件状態</TableCell>
                <TableCell>担当者</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={sortBy === 'expected_order_amount'}
                    direction={sortBy === 'expected_order_amount' ? sortOrder : 'asc'}
                    onClick={() => handleSortChange('expected_order_amount')}
                  >
                    受注予定額
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {selectedFiscalYear === 'current' ? `${currentYear}年度計上` : `${nextYear}年度計上`}
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'expected_order_date'}
                    direction={sortBy === 'expected_order_date' ? sortOrder : 'asc'}
                    onClick={() => handleSortChange('expected_order_date')}
                  >
                    受注予定日
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cases.length > 0 ? (
                cases.map((salesCase) => (
                  <TableRow
                    key={salesCase.case_id}
                    hover
                    sx={salesCase.updated_this_week ? {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08) !important' },
                    } : undefined}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{salesCase.account_name || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {salesCase.updated_this_week && (
                          <Tooltip title={`今週更新あり（${salesCase.updated_at ? formatDateJST(salesCase.updated_at) : ''}）`}>
                            <FiberNew sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
                          </Tooltip>
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{salesCase.case_name}</span>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={salesCase.sales_phase_name || '-'}
                        size="small"
                        sx={{
                          backgroundColor: salesCase.phase_color_code || '#e0e0e0',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: salesCase.sales_phase_code === 'EXECUTION' ? 'text.disabled' :
                                 (salesCase.days_in_phase || 0) > (salesCase.target_days ?? 14) ? 'error.main' : 'text.secondary',
                          fontWeight: salesCase.sales_phase_code !== 'EXECUTION'
                            && (salesCase.days_in_phase || 0) > (salesCase.target_days ?? 14) ? 'bold' : 'normal'
                        }}
                      >
                        {salesCase.sales_phase_code === 'EXECUTION' ? '-' : `${salesCase.days_in_phase || 0}日`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {salesCase.next_action_date ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: new Date(salesCase.next_action_date) < new Date() ? 'error.main' : 'text.primary'
                          }}
                        >
                          {formatDate(salesCase.next_action_date)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          // approval_statusによるステータス表示
                          salesCase.approval_status === 'draft' ? '新規下書き' :
                          salesCase.approval_status === 'new_pending' ? '新規承認待ち' :
                          salesCase.approval_status === 'pending' ? '変更承認待ち' :
                          salesCase.approval_status === 'rejected' ? '変更却下' :
                          // 通常のstatusによる表示
                          salesCase.status === 'active' ? '商談中' :
                          salesCase.status === 'suspended' ? '保留' :
                          salesCase.close_result === 'won' ? '受注' : '失注'
                        }
                        size="small"
                        color={
                          // approval_statusによる色分け
                          salesCase.approval_status === 'draft' ? 'default' :
                          salesCase.approval_status === 'new_pending' ? 'secondary' :
                          salesCase.approval_status === 'pending' ? 'info' :
                          salesCase.approval_status === 'rejected' ? 'error' :
                          // 通常のstatusによる色分け
                          salesCase.status === 'active' ? 'primary' :
                          salesCase.status === 'suspended' ? 'warning' :
                          salesCase.status === 'draft' ? 'secondary' :
                          salesCase.close_result === 'won' ? 'success' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{salesCase.assigned_employee_name || '-'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {formatCurrencyWithUnit(salesCase.expected_order_amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {formatCurrencyWithUnit(salesCase.fiscal_year_planned_sales)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(salesCase.expected_order_date)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="詳細">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/sales/cases/${salesCase.case_id}`)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {((salesCase.assigned_employee_id === user?.employee_id) || salesPermissions?.can_manage_accounts) && (
                          <Tooltip title={
                            salesCase.approval_status === 'pending' || salesCase.approval_status === 'new_pending' 
                              ? '承認待ち' 
                              : '編集'
                          }>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/sales/cases/${salesCase.case_id}/edit`)}
                                disabled={salesCase.approval_status === 'pending' || salesCase.approval_status === 'new_pending'}
                              >
                                <Edit />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      案件が見つかりませんでした
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ページネーション */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default SalesCasesPage;
